import React, { useState } from "react";
import { Plus, Search, Users, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { useList, useDeleteOne, useBulkDelete } from "@/hooks/useApi";
import { useAuth } from "@/context/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader, Loading, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const SHIFTS = ["Shift A (6am-2pm)", "Shift B (2pm-10pm)", "Shift C (10pm-6am)"];
const DESIGNATIONS = ["Operator", "Supervisor", "Line Leader", "QC Inspector", "Cutter", "Printer", "Packer", "Sample Master"];

export default function Employees() {
  const { canWrite, user } = useAuth();
  const qc = useQueryClient();
  const delOne = useDeleteOne((id) => `/employees/${id}`, ["employees"]);
  const clearAll = useBulkDelete((e) => `/employees/${e.id}`, ["employees"]);
  const { data: employees, isLoading } = useList("employees", "/employees");
  const { data: departments = [] } = useList("departments", "/departments");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", phone: "", department: "", designation: "", shift: "", joining_date: "" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.name) return toast.error("Name required");
    try { await api.post("/employees", f); toast.success("Employee added"); qc.invalidateQueries(); setOpen(false); setF({ name: "", phone: "", department: "", designation: "", shift: "", joining_date: "" }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const list = (employees || []).filter((e) => !search || (e.name + e.employee_code + (e.department || "")).toLowerCase().includes(search.toLowerCase()));
  if (isLoading) return <Loading />;
  return (
    <div>
      <PageHeader title="Employees & Workers" subtitle="Workforce master" testid="employees-header">
        {canWrite("employees") && <Button onClick={() => setOpen(true)} className="gap-2" data-testid="new-employee-btn"><Plus className="w-4 h-4" /> Add Employee</Button>}
      </PageHeader>
      <Card className="p-3 mb-4 relative">
        <Search className="w-4 h-4 absolute left-6 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input placeholder="Search employees…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="employees-search" />
      </Card>
      <Card className="overflow-hidden">
        {!list.length ? <EmptyState title="No employees" /> : (
          <div className="overflow-x-auto"><Table>
            <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Department</TableHead><TableHead>Designation</TableHead><TableHead>Shift</TableHead><TableHead>Joined</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>{list.map((e) => (
              <TableRow key={e.id} data-testid={`employee-row-${e.employee_code}`}>
                <TableCell className="font-num text-primary">{e.employee_code}</TableCell>
                <TableCell className="font-medium flex items-center gap-2"><div className="w-7 h-7 rounded-full bg-primary/15 text-primary flex items-center justify-center text-xs font-bold">{e.name?.[0]}</div>{e.name}</TableCell>
                <TableCell className="text-sm">{e.department}</TableCell><TableCell className="text-sm">{e.designation}</TableCell>
                <TableCell className="text-sm">{e.shift}</TableCell><TableCell className="text-sm">{e.joining_date}</TableCell>
                <TableCell><Badge variant={e.active !== false ? "default" : "secondary"}>{e.active !== false ? "Active" : "Inactive"}</Badge></TableCell>
                <TableCell>{canWrite("employees") && <Button size="icon" variant="ghost" onClick={() => delOne(e.id, `Delete ${e.name}?`)} data-testid={`delete-employee-${e.employee_code}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>}</TableCell>
              </TableRow>
            ))}</TableBody>
          </Table></div>
        )}
      </Card>
      <Dialog open={open} onOpenChange={setOpen}><DialogContent data-testid="employee-form">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Users className="w-5 h-5" /> Add Employee</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Name</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="employee-name-input" /></div>
          <div><Label>Phone</Label><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} /></div>
          <div><Label>Department</Label>
            <Select value={f.department} onValueChange={(v) => set("department", v)}>
              <SelectTrigger><SelectValue placeholder="Dept" /></SelectTrigger>
              <SelectContent>{departments.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Designation</Label>
            <Select value={f.designation} onValueChange={(v) => set("designation", v)}>
              <SelectTrigger><SelectValue placeholder="Role" /></SelectTrigger>
              <SelectContent>{DESIGNATIONS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Shift</Label>
            <Select value={f.shift} onValueChange={(v) => set("shift", v)}>
              <SelectTrigger><SelectValue placeholder="Shift" /></SelectTrigger>
              <SelectContent>{SHIFTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Joining Date</Label><Input type="date" value={f.joining_date} onChange={(e) => set("joining_date", e.target.value)} /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit} data-testid="employee-save-btn">Add</Button></DialogFooter>
      </DialogContent></Dialog>
    </div>
  );
}
